// ----- Phase E-polish-extract-4 — shared toast state hook ----- //
//
// All 6 admin pages (SpecFiles, PromptLibrary, FormTemplates,
// CompanyProfile, Users, Settings) repeated the same toast state
// management verbatim:
//
//   const [toast, setToast] = useState<ToastMessage | null>(null);
//   const showToast = useCallback((msg: ToastMessage) => {
//     setToast(msg);
//     if (msg.type === 'success') {
//       globalThis.setTimeout(() => setToast(null), 5000);
//     }
//   }, []);
//
// This hook replaces that block. Auto-dismiss timing (5000ms on
// success only) is unchanged.
//
// Exactly one dismiss timer may be pending at a time, and it must never
// outlive the toast it was armed for: a stale timer firing after the
// toast was replaced dismisses the WRONG toast (a success timer silently
// closing a later error), and one firing after unmount updates
// unmounted state (the PromptLibraryPage post-teardown crash). Every
// path that changes the toast — show, clear, unmount — cancels the
// pending timer first.

import { useCallback, useEffect, useRef, useState } from 'react';
import { type ToastMessage } from '@/components/shared/PageToast';

const SUCCESS_AUTO_DISMISS_MS = 5000;

export interface UseUiToastResult {
  readonly toast: ToastMessage | null;
  readonly showToast: (msg: ToastMessage) => void;
  readonly clearToast: () => void;
}

export function useUiToast(): UseUiToastResult {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  const cancelDismissTimer = useCallback((): void => {
    if (dismissTimerRef.current !== null) {
      globalThis.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelDismissTimer, [cancelDismissTimer]);

  const showToast = useCallback(
    (msg: ToastMessage): void => {
      cancelDismissTimer();
      setToast(msg);
      // A toast that carries an action (e.g. Undo) stays until dismissed.
      if (msg.type === 'success' && !msg.action) {
        dismissTimerRef.current = globalThis.setTimeout(() => {
          dismissTimerRef.current = null;
          setToast(null);
        }, SUCCESS_AUTO_DISMISS_MS);
      }
    },
    [cancelDismissTimer],
  );

  const clearToast = useCallback((): void => {
    cancelDismissTimer();
    setToast(null);
  }, [cancelDismissTimer]);

  return { toast, showToast, clearToast };
}
